import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    ClipboardList,
    Plus,
    Loader2,
    FileText,
} from "lucide-react";
import {
    individualsApi,
    plansApi,
    outcomesApi,
    activitiesApi,
    tasksApi,
} from "@/lib/planApi";

const StatusBadge = ({ status }) => {
    const map = {
        draft: "bg-amber-100 text-amber-800",
        active: "bg-brand-green-100 text-brand-green-800",
        inactive: "bg-slate-200 text-slate-700",
    };
    return (
        <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                map[status] || "bg-slate-200 text-slate-700"
            }`}
        >
            {status}
        </span>
    );
};

// English-only helpers -----------------------------------------------------
import { formatDateUS } from "@/lib/dates";
const formatDate = formatDateUS;

const pluralize = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

const formatSummary = (counts) => {
    if (!counts) return "—";
    const { outcomes, activities, tasks } = counts;
    return `${pluralize(outcomes, "outcome", "outcomes")}, ${pluralize(
        activities,
        "activity",
        "activities",
    )} and ${pluralize(tasks, "task", "tasks")}`;
};

const IndividualDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ind, setInd] = useState(null);
    const [plans, setPlans] = useState([]);
    const [outcomes, setOutcomes] = useState([]);
    const [activities, setActivities] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [individual, list, allOutcomes, allActivities, allTasks] =
                await Promise.all([
                    individualsApi.get(id),
                    plansApi.listSlim({ individual_id: id }),
                    outcomesApi.list().catch(() => []),
                    activitiesApi.list().catch(() => []),
                    tasksApi.list().catch(() => []),
                ]);
            setInd(individual);
            setPlans(list);
            setOutcomes(Array.isArray(allOutcomes) ? allOutcomes : []);
            setActivities(Array.isArray(allActivities) ? allActivities : []);
            setTasks(Array.isArray(allTasks) ? allTasks : []);
        } catch (err) {
            toast.error(err?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Precompute counts per plan: outcomes → activities → tasks.
    const summaryByPlan = useMemo(() => {
        const outcomesByPlan = new Map();
        const activitiesByOutcome = new Map();
        const tasksByActivity = new Map();
        outcomes.forEach((o) => {
            if (!outcomesByPlan.has(o.plan_id)) outcomesByPlan.set(o.plan_id, []);
            outcomesByPlan.get(o.plan_id).push(o);
        });
        activities.forEach((a) => {
            if (!activitiesByOutcome.has(a.outcome_id))
                activitiesByOutcome.set(a.outcome_id, []);
            activitiesByOutcome.get(a.outcome_id).push(a);
        });
        tasks.forEach((t) => {
            if (!tasksByActivity.has(t.activity_id))
                tasksByActivity.set(t.activity_id, []);
            tasksByActivity.get(t.activity_id).push(t);
        });
        const summary = {};
        plans.forEach((p) => {
            const os = outcomesByPlan.get(p.id) || [];
            let acts = 0;
            let tks = 0;
            os.forEach((o) => {
                const as = activitiesByOutcome.get(o.id) || [];
                acts += as.length;
                as.forEach((a) => {
                    tks += (tasksByActivity.get(a.id) || []).length;
                });
            });
            summary[p.id] = { outcomes: os.length, activities: acts, tasks: tks };
        });
        return summary;
    }, [plans, outcomes, activities, tasks]);

    const handleCreatePlan = async () => {
        if (!newTitle.trim()) return;
        setCreating(true);
        try {
            const created = await plansApi.create({
                individual_id: id,
                title: newTitle,
                status: "draft",
            });
            toast.success("Plan created");
            setNewTitle("");
            navigate(`/plans/${created.id}`);
        } catch (err) {
            toast.error(err?.message || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-16 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
                individual…
            </div>
        );
    }
    if (!ind) return null;

    return (
        <div
            className="mx-auto max-w-7xl"
            data-testid="individual-detail-page"
        >
            <Link
                to="/individuals"
                className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-blue-700"
            >
                <ArrowLeft className="h-4 w-4" /> Back to individuals
            </Link>

            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                        Individual
                    </p>
                    <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                        {ind.first_name} {ind.last_name}
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">
                        {ind.disability_type || "—"} ·{" "}
                        {ind.disability_level || "—"}
                        {ind.waiver_type ? ` · ${ind.waiver_type} waiver` : ""}
                    </p>
                    {Array.isArray(ind.diagnoses) && ind.diagnoses.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {ind.diagnoses.map((d) => (
                                <span
                                    key={d}
                                    className="rounded-full bg-brand-blue-50 px-2.5 py-1 text-xs font-medium text-brand-blue-800"
                                >
                                    {d}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-heading text-lg font-bold text-brand-blue-950">
                        <ClipboardList className="mr-2 inline h-5 w-5 text-brand-blue-700" />
                        Plans
                    </h2>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="New plan title…"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            data-testid="new-plan-title-input"
                            className="h-10 w-64 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20"
                        />
                        <button
                            onClick={handleCreatePlan}
                            disabled={creating || !newTitle.trim()}
                            data-testid="create-plan-button"
                            className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-4 text-sm font-semibold text-white hover:bg-brand-blue-800 disabled:opacity-70"
                        >
                            {creating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4" />
                            )}
                            New plan
                        </button>
                    </div>
                </div>

                {plans.length === 0 ? (
                    <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
                        <FileText className="mx-auto h-10 w-10 text-slate-300" />
                        <p className="mt-3 font-semibold text-slate-700">
                            No plans yet
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                            Create a plan and upload the ISP PDF to auto-build
                            the outcome tree with Claude.
                        </p>
                    </div>
                ) : (
                    <div data-testid="plans-list">
                        {/* Desktop table (md+) */}
                        <div className="hidden overflow-x-auto rounded-xl border border-slate-100 md:block">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                                        <th className="px-4 py-3">Plan Name</th>
                                        <th className="px-4 py-3">Summary</th>
                                        <th className="px-4 py-3">Start Date</th>
                                        <th className="px-4 py-3">End Date</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Created At</th>
                                        <th className="px-4 py-3">Created By</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {plans.map((p) => (
                                        <tr
                                            key={p.id}
                                            className="hover:bg-slate-50"
                                            data-testid="plan-row"
                                        >
                                            <td className="px-4 py-3">
                                                <Link
                                                    to={`/plans/${p.id}`}
                                                    className="font-semibold text-brand-blue-800 hover:text-brand-blue-950"
                                                    data-testid="plan-open-link"
                                                >
                                                    {p.title}
                                                </Link>
                                            </td>
                                            <td
                                                className="px-4 py-3 text-slate-600"
                                                data-testid="plan-col-summary"
                                            >
                                                {formatSummary(summaryByPlan[p.id])}
                                            </td>
                                            <td
                                                className="px-4 py-3 text-slate-600"
                                                data-testid="plan-col-start"
                                            >
                                                {formatDate(p.start_date)}
                                            </td>
                                            <td
                                                className="px-4 py-3 text-slate-600"
                                                data-testid="plan-col-end"
                                            >
                                                {formatDate(p.end_date)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={p.status} />
                                            </td>
                                            <td
                                                className="px-4 py-3 text-slate-600"
                                                data-testid="plan-col-created-at"
                                            >
                                                {formatDate(p.created_at)}
                                            </td>
                                            <td
                                                className="px-4 py-3 text-slate-600"
                                                data-testid="plan-col-created-by"
                                            >
                                                {p.created_by_name || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile stacked cards (<md) */}
                        <div className="space-y-3 md:hidden">
                            {plans.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid="plan-row"
                                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <Link
                                            to={`/plans/${p.id}`}
                                            className="font-semibold text-brand-blue-800 hover:text-brand-blue-950"
                                            data-testid="plan-open-link"
                                        >
                                            {p.title}
                                        </Link>
                                        <StatusBadge status={p.status} />
                                    </div>
                                    <dl className="mt-3 grid grid-cols-2 gap-y-2 text-xs">
                                        <dt className="text-slate-400">Summary</dt>
                                        <dd
                                            className="text-slate-700"
                                            data-testid="plan-col-summary"
                                        >
                                            {formatSummary(summaryByPlan[p.id])}
                                        </dd>
                                        <dt className="text-slate-400">Start</dt>
                                        <dd
                                            className="text-slate-700"
                                            data-testid="plan-col-start"
                                        >
                                            {formatDate(p.start_date)}
                                        </dd>
                                        <dt className="text-slate-400">End</dt>
                                        <dd
                                            className="text-slate-700"
                                            data-testid="plan-col-end"
                                        >
                                            {formatDate(p.end_date)}
                                        </dd>
                                        <dt className="text-slate-400">Created</dt>
                                        <dd
                                            className="text-slate-700"
                                            data-testid="plan-col-created-at"
                                        >
                                            {formatDate(p.created_at)}
                                        </dd>
                                        <dt className="text-slate-400">By</dt>
                                        <dd
                                            className="text-slate-700"
                                            data-testid="plan-col-created-by"
                                        >
                                            {p.created_by_name || "—"}
                                        </dd>
                                    </dl>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default IndividualDetailPage;
